import {
  ANONYMOUS_POLL_MIN_RESPONSES,
  type PollResultsWithheld,
} from '@cloud/shared/projects/polls'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Check, Copy, Globe, Link2, Mail, Plus, RefreshCw, X } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useListFilter } from '@/components/layout/record-nav-context'
import { useRecordNavigator } from '@/components/layout/use-record-navigator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUrlTab } from '@/hooks/use-url-tab'
import { errorMessage } from '@/lib/error-message'
import { BACKEND_URL, orpc } from '@/lib/orpcClient'
import { isVersionConflict } from '@/lib/version-conflict'
import {
  type Audience,
  draftFromPoll,
  draftToUpdateInput,
  prefillsOwnAnswers,
  respondAction,
  resultsHiddenKey,
  type SurveyDraft,
  validateDraft,
} from './-survey'
import { SurveyBuilder } from './-survey-builder'
import {
  type RAnswer,
  RespondForm,
  type RQuestion,
  type SurveyResults,
  SurveyResultsView,
} from './-survey-respond'
import { useAudiencePickers } from './-use-audience-pickers'

export const Route = createFileRoute('/projects/polls/$id')({
  component: PollDetailPage,
})

type EffectiveStatus = 'draft' | 'scheduled' | 'open' | 'closed'

interface Invite {
  id: string
  email: string
  name: string | null
  token: string
  sentAt: string | null
  respondedAt: string | null
}

interface PollDetail {
  id: string
  title: string
  description: string | null
  projectId: string | null
  status: string
  audience: Audience
  effectiveStatus: EffectiveStatus
  anonymous: boolean
  publicToken: string | null
  resultsVisibility: string
  allowMultipleResponses: boolean
  requireRespondentName: boolean
  closingMessage: string | null
  startsAt: string | null
  endsAt: string | null
  version: number
  isOwnerOrAdmin: boolean
  canRespond: boolean
  questions: RQuestion[]
  audienceRefs: Array<{ kind: 'user' | 'department'; refId: string }>
  invites: Invite[]
  /** Ook op een anonieme poll, waar `myResponse` altijd leeg is. */
  hasResponded: boolean
  myResponse: { id: string; answers: RAnswer[] } | null
  results: SurveyResults | null
  /** Waarom een anonieme enquête (nog) geen resultaten toont. */
  resultsWithheld: PollResultsWithheld | null
}

const STATUS_VARIANT: Record<EffectiveStatus, string> = {
  open: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-rose-100 text-rose-800',
  scheduled: 'bg-amber-100 text-amber-800',
  draft: 'bg-muted text-muted-foreground',
}

function PollDetailPage() {
  const { id } = Route.useParams()
  const t = useTranslations()
  const tk = (k: string) => t(`projects.pollsPage.${k}`)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useUrlTab('respond')

  const pollQ = useQuery(orpc.projects.polls.get.queryOptions({ input: { id } }))
  const poll = pollQ.data as PollDetail | undefined
  const invalidate = () => qc.invalidateQueries({ queryKey: orpc.projects.polls.key() })

  const projectsQ = useQuery(orpc.projects.projects.list.queryOptions({ input: { limit: 500 } }))
  const projectOptions = ((projectsQ.data?.rows ?? []) as Array<{ id: string; name: string }>).map(
    (p) => ({
      value: p.id,
      label: p.name,
    }),
  )

  // De derde wiring van de gedeelde gevonden set: de lijst publiceert zoekterm, sortering
  // ÉN filters via `useServerList`, en vorige/volgende vraagt zijn buren over exact die set
  // op. Laat je `filters` hier weg, dan versmalt het project- of statusfilter wél de tabel
  // maar niet `position / total`, en meldt rij 1 "2 / 4".
  const { filter } = useListFilter('poll')
  const neighborsQ = useQuery(
    orpc.projects.polls.neighbors.queryOptions({
      input: {
        id,
        search: filter.search || undefined,
        sort: filter.sort,
        filters: filter.filters,
      },
    }),
  )
  // `useRecordNavigator` rechtstreeks, niet de `useEntityNav`-wikkel: die eist een
  // verplichte `toNew`, en een peiling ontstaat alleen via de bouwer op de lijstpagina —
  // er is dus geen `/new`-route om naartoe te sturen. Zelfde constructie als de pakbon.
  useRecordNavigator({
    entityKey: 'poll',
    id,
    neighbors: neighborsQ.data,
    goTo: (nextId) =>
      navigate({ to: '/projects/polls/$id', params: { id: nextId }, search: (prev) => prev }),
    toList: () => navigate({ to: '/projects/polls' }),
  })

  const setStatusMutation = useMutation(
    orpc.projects.polls.setStatus.mutationOptions({
      onSuccess: () => {
        toast.success(tk('toast.updated'))
        invalidate()
      },
      onError: (e: Error) => {
        if (isVersionConflict(e)) {
          toast.error(t('common.versionConflict'))
          invalidate()
        } else {
          toast.error(errorMessage(e, t))
        }
      },
    }),
  )

  if (pollQ.isLoading) {
    return <div className="p-6 text-muted-foreground">{t('projects.common.loading')}</div>
  }
  if (!poll) {
    return (
      <div className="p-6 space-y-4">
        <Button variant="ghost" onClick={() => navigate({ to: '/projects/polls' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {tk('backToList')}
        </Button>
        <p className="text-muted-foreground">{tk('notFound')}</p>
      </div>
    )
  }

  const setStatus = (status: 'draft' | 'open' | 'closed') =>
    setStatusMutation.mutate({ id: poll.id, status, expectedVersion: poll.version })

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div>
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2"
          onClick={() => navigate({ to: '/projects/polls' })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {tk('backToList')}
        </Button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{poll.title}</h1>
            {poll.description && <p className="text-muted-foreground mt-1">{poll.description}</p>}
            <p className="text-xs text-muted-foreground mt-1">
              {t(`projects.pollsPage.audience.${poll.audience}`)}
            </p>
          </div>
          <Badge variant="outline" className={STATUS_VARIANT[poll.effectiveStatus]}>
            {t(`projects.pollsPage.status.${poll.effectiveStatus}`)}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {(['draft', 'open', 'closed'] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={poll.status === s ? 'default' : 'outline'}
              disabled={setStatusMutation.isPending}
              onClick={() => setStatus(s)}
            >
              {t(`projects.pollsPage.setStatus.${s}`)}
            </Button>
          ))}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="respond">{tk('tabs.respond')}</TabsTrigger>
          <TabsTrigger value="results">{tk('tabs.results')}</TabsTrigger>
          {/* Zonder token (een veldrecht verbergt `publicToken`) is er niets te delen: de link
              bestaat al vanaf het maken, dus een knop om hem te maken kan niets doen. */}
          {poll.audience === 'link' && poll.publicToken && (
            <TabsTrigger value="share">{tk('tabs.share')}</TabsTrigger>
          )}
          {poll.audience === 'invite' && (
            <TabsTrigger value="invites">{tk('tabs.invites')}</TabsTrigger>
          )}
          <TabsTrigger value="settings">{tk('tabs.settings')}</TabsTrigger>
        </TabsList>

        <TabsContent value="respond">
          <Card>
            <CardContent className="py-6">
              {poll.audience === 'link' || poll.audience === 'invite' ? (
                <p className="text-sm text-muted-foreground">{tk('externalAudienceHint')}</p>
              ) : (
                <RespondForm
                  key={poll.myResponse?.id ?? (poll.hasResponded ? 'responded' : 'new')}
                  pollId={poll.id}
                  questions={poll.questions}
                  prior={
                    prefillsOwnAnswers(respondAction(poll)) ? poll.myResponse?.answers : undefined
                  }
                  open={poll.effectiveStatus === 'open'}
                  anonymous={poll.anonymous}
                  canRespond={poll.canRespond}
                  hasResponded={poll.hasResponded}
                  action={respondAction(poll)}
                  onSubmitted={invalidate}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results">
          <Card>
            <CardContent className="py-6">
              {poll.results ? (
                <SurveyResultsView results={poll.results} />
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t(`projects.pollsPage.${resultsHiddenKey(poll)}`, {
                    count: String(ANONYMOUS_POLL_MIN_RESPONSES),
                  })}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {poll.audience === 'link' && poll.publicToken && (
          <TabsContent value="share">
            <ShareTab poll={poll} token={poll.publicToken} onChange={invalidate} />
          </TabsContent>
        )}

        {poll.audience === 'invite' && (
          <TabsContent value="invites">
            <InvitesTab poll={poll} onChange={invalidate} />
          </TabsContent>
        )}

        <TabsContent value="settings">
          <SettingsTab
            // De tab zaait `draft` één keer bij het monteren. De versie hoort in de
            // sleutel zodat een opslag of een CAS-misser hem opnieuw zaait op serverwaarheid;
            // de id blijft erin, anders deelt een andere peiling met hetzelfde versienummer
            // de gemonteerde staat.
            key={`${poll.id}:${poll.version}`}
            poll={poll}
            projectOptions={projectOptions}
            onChange={invalidate}
            onDeleted={() => {
              invalidate()
              navigate({ to: '/projects/polls' })
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function CopyableLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () =>
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  return (
    <div className="flex gap-2">
      <Input readOnly value={url} className="font-mono text-xs" />
      <Button variant="outline" size="icon" onClick={copy}>
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

function ShareTab({
  poll,
  token,
  onChange,
}: {
  poll: PollDetail
  token: string
  onChange: () => void
}) {
  const t = useTranslations()
  const tk = (k: string) => t(`projects.pollsPage.${k}`)
  const url = `${BACKEND_URL}/p/${token}`

  const setAudienceMutation = useMutation(
    orpc.projects.polls.setAudience.mutationOptions({
      onSuccess: () => {
        toast.success(tk('toast.updated'))
        onChange()
      },
      onError: (e: Error) => {
        if (isVersionConflict(e)) {
          toast.error(t('common.versionConflict'))
          onChange()
        } else {
          toast.error(errorMessage(e, t))
        }
      },
    }),
  )

  return (
    <Card>
      <CardContent className="py-6 space-y-5">
        <div>
          <div className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {tk('publicLink')}
          </div>
          <div className="text-xs text-muted-foreground">{tk('publicLinkHint')}</div>
        </div>

        <CopyableLink url={url} />

        <div className="flex flex-col items-center gap-3 rounded-lg border p-5">
          <QRCodeSVG value={url} size={160} />
          <p className="text-xs text-muted-foreground">{tk('qrHint')}</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <Link2 className="h-4 w-4 mr-1" />
              {tk('openLink')}
            </a>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-1" />
                {tk('regenerate')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{tk('regenerateTitle')}</AlertDialogTitle>
                <AlertDialogDescription>{tk('regenerateBody')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    setAudienceMutation.mutate({
                      id: poll.id,
                      audience: 'link',
                      regenerateLink: true,
                      expectedVersion: poll.version,
                    })
                  }
                >
                  {tk('regenerate')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  )
}

function InvitesTab({ poll, onChange }: { poll: PollDetail; onChange: () => void }) {
  const t = useTranslations()
  const tk = (k: string) => t(`projects.pollsPage.${k}`)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')

  const addMutation = useMutation(
    orpc.projects.polls.addInvites.mutationOptions({
      onSuccess: () => {
        toast.success(tk('toast.invitesSent'))
        setEmail('')
        setName('')
        onChange()
      },
      onError: (e: Error) => toast.error(errorMessage(e, t)),
    }),
  )
  const removeMutation = useMutation(
    orpc.projects.polls.removeInvite.mutationOptions({
      onSuccess: onChange,
      onError: (e: Error) => toast.error(errorMessage(e, t)),
    }),
  )

  const add = () => {
    if (!email.trim()) return
    addMutation.mutate({
      pollId: poll.id,
      invites: [{ email: email.trim(), name: name.trim() || null }],
    })
  }

  return (
    <Card>
      <CardContent className="py-6 space-y-5">
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder={tk('builder.inviteEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            placeholder={tk('builder.inviteName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="max-w-[12rem]"
          />
          <Button disabled={!email.trim() || addMutation.isPending} onClick={add}>
            <Plus className="h-4 w-4 mr-1" />
            {tk('invite')}
          </Button>
        </div>

        {poll.invites.length === 0 ? (
          <p className="text-sm text-muted-foreground">{tk('noInvites')}</p>
        ) : (
          <div className="space-y-2">
            {poll.invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 rounded-lg border p-3">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{inv.name || inv.email}</div>
                  {inv.name && (
                    <div className="text-xs text-muted-foreground truncate">{inv.email}</div>
                  )}
                </div>
                {inv.respondedAt ? (
                  <Badge variant="outline" className="bg-emerald-100 text-emerald-800">
                    {tk('responded')}
                  </Badge>
                ) : (
                  <Badge variant="outline">{tk('pending')}</Badge>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(`${BACKEND_URL}/p/i/${inv.token}`)
                    toast.success(tk('linkCopied'))
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={removeMutation.isPending}
                  onClick={() => removeMutation.mutate({ id: inv.id })}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function SettingsTab({
  poll,
  projectOptions,
  onChange,
  onDeleted,
}: {
  poll: PollDetail
  projectOptions: Array<{ value: string; label: string }>
  onChange: () => void
  onDeleted: () => void
}) {
  const t = useTranslations()
  const tk = (k: string) => t(`projects.pollsPage.${k}`)
  const [draft, setDraft] = useState<SurveyDraft>(draftFromPoll(poll))
  const { userOptions, departmentOptions, isLoading, isError } = useAudiencePickers()

  const updateMutation = useMutation(
    orpc.projects.polls.update.mutationOptions({
      onSuccess: () => {
        toast.success(tk('toast.updated'))
        onChange()
      },
      onError: (e: Error) => {
        if (isVersionConflict(e)) {
          toast.error(t('common.versionConflict'))
          onChange()
        } else {
          toast.error(errorMessage(e, t))
        }
      },
    }),
  )
  const deleteMutation = useMutation(
    orpc.projects.polls.delete.mutationOptions({
      onSuccess: () => {
        toast.success(tk('toast.deleted'))
        onDeleted()
      },
      onError: (e: Error) => toast.error(errorMessage(e, t)),
    }),
  )

  return (
    <Card>
      <CardContent className="py-6 space-y-5">
        <SurveyBuilder
          draft={draft}
          setDraft={setDraft}
          projectOptions={projectOptions}
          userOptions={userOptions}
          departmentOptions={departmentOptions}
          directoryLoading={isLoading}
          directoryError={isError}
          mode="edit"
        />
        <div className="flex justify-between pt-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={deleteMutation.isPending}>
                {t('projects.common.delete')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('projects.common.deleteConfirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription>{tk('deleteBody')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteMutation.mutate({ id: poll.id })}>
                  {t('projects.common.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            disabled={!validateDraft(draft, 'edit') || updateMutation.isPending}
            onClick={() =>
              updateMutation.mutate({
                ...draftToUpdateInput(poll.id, draft),
                expectedVersion: poll.version,
              })
            }
          >
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
